import "whatwg-fetch";
import { forOwn, upperCase } from "lodash";
import * as Url from "url-parse";
import { HttpTokenUtils, UserToken } from "./get-token";
import { Timer } from "./timer";
import { ParamsParserUtils } from "./params-parser-utils";
import { IHttpOptions, IHttp, RESPONSE_TYPES } from "@lib/common-interfaces";
import { FetchError } from "./fetch-error";
import { Utils } from "./utils";
import { CustomDimensions, telemetry } from "../telemetry";
import { config } from "../utils/config";
const uuid = require("uuid");

/**
 * All the methods!
 */
export const METHODS = {
  HEAD: "HEAD",
  GET: "GET",
  POST: "POST",
  PATCH: "PATCH",
  PUT: "PUT",
  DELETE: "DELETE"
};

/**
 * Http library
 *
 * @desc Our custom wrapper for Http. This library uses the `fetch` library underneath its covers.
 * This library encapsulates the action of fetching the authentication token which is required to
 * talk to our backend API's.
 *
 * This library accepts parameterized urls and can parse them with corresponding params to create urls.
 *
 * @export
 * @class Http
 * @implements {IHttp}
 */
export class Http implements IHttp {
  private _rootUrl: string;

  constructor(rootUrl?: string) {
    this._rootUrl = rootUrl || "";
  }

  /**
   * Http HEAD
   *
   * @template T
   * @param {string} url
   * @param {IHttpOptions} [options={}]
   * @returns {Promise<T>}
   */
  public head<T>(url: string, options: IHttpOptions = {}): Promise<T> {
    return this._call<T>(METHODS.HEAD, url, options);
  }

  /**
   * Http GET
   *
   * @template T
   * @param {string} url
   * @param {IHttpOptions} [options={}]
   * @returns {Promise<T>}
   */
  public get<T>(url: string, options: IHttpOptions = {}): Promise<T> {
    return this._call<T>(METHODS.GET, url, options);
  }

  /**
   * Http POST
   *
   * @template T
   * @param {string} url
   * @param {IHttpOptions} [options={}]
   * @returns {Promise<T>}
   */
  public post<T>(url: string, options: IHttpOptions = {}): Promise<T> {
    return this._call<T>(METHODS.POST, url, options);
  }

  /**
   * Http PUT
   *
   * @template T
   * @param {string} url
   * @param {IHttpOptions} [options={}]
   * @returns {Promise<T>}
   */
  public put<T>(url: string, options: IHttpOptions = {}): Promise<T> {
    return this._call<T>(METHODS.PUT, url, options);
  }

  /**
   * Http PATCH
   *
   * @template T
   * @param {string} url
   * @param {IHttpOptions} [options={}]
   * @returns {Promise<T>}
   */
  public patch<T>(url: string, options: IHttpOptions = {}): Promise<T> {
    return this._call<T>(METHODS.PATCH, url, options);
  }

  /**
   * Http DELETE
   *
   * @template T
   * @param {string} url
   * @param {IHttpOptions} [options={}]
   * @returns {Promise<T>}
   */
  public delete<T>(url: string, options: IHttpOptions = {}): Promise<T> {
    return this._call<T>(METHODS.DELETE, url, options);
  }

  /**
   * Calls the fetch library using the method provided.
   *
   * @template T
   * @param {string} method - The Http verb
   * @param {string} url
   * @param {IHttpOptions} [options]
   * @returns {Promise<T>}
   */
  public call<T>(method: string, url: string, options: IHttpOptions): Promise<T> {
    return this._call<T>(method, url, options);
  }

  /**
   * Core function which makes the Http call using the fetch library.
   *
   * @private
   * @template T
   * @param {string} method
   * @param {string} url
   * @param {IHttpOptions} options
   * @returns {Promise<T>}
   */
  private _call<T>(method: string, url: string, options: IHttpOptions): Promise<T> {
    const operationId = uuid.v4();
    // Track the url before applying params, so that it is easier to group telemetry
    const urlTemplate = url;

    // Add headers
    const headers = new Headers();
    // Add diagnostc context id
    headers.append("diagnostic-context", operationId);
    // Add source header to track in API gateway
    headers.append("internal-request-source", "portal");

    // Append passed in headers
    forOwn(options.headers || {}, (value, key) => {
      headers.append(key!, value);
    });

    // Add params
    let params = options.params || {};
    const parsedUrl = new Url(url, "/"); /** Setting no base url if the url to be parsed is relative */
    let path = parsedUrl.pathname;
    const protocol = parsedUrl.protocol;
    const host = parsedUrl.host;
    const noCacheParam = { nocache: Utils.randomCacheBusterValue() };

    // Add no cache param if the browser is IE.
    params = Object.assign({}, params, Utils.isIe() ? noCacheParam : {});

    // Parse the path with the params provided
    path = ParamsParserUtils.parse(protocol.length === 0 && !path.startsWith("/") ? `/${path}` : path, params);

    // Build the URL
    // If the url provided is an absolute url, use the protocol & host from that url to create the new one.
    // If the url provided is a relative url, use the rootUrl -- which can be empty or set to a specific value.
    url = protocol ? `${protocol}//${host}${path}` : `${this._rootUrl}${path}`;

    // Prepare requestOptions for fetch
    method = upperCase(method);
    const requestOptions: RequestInit = {
      method: method,
      headers: headers,
      credentials: "same-origin" // enable cookies
    };

    // Add Content-Type header & body for the right methods
    if (method === METHODS.POST || method === METHODS.PUT || method === METHODS.PATCH || method === METHODS.DELETE) {
      if (!options.noJson) {
        headers.append("Content-Type", "application/json");
        const defaultBody = method === METHODS.DELETE ? undefined : {};
        requestOptions["body"] = JSON.stringify(options.body || defaultBody);
      } else {
        requestOptions["body"] = options.body;
      }
    }

    const { forceRefreshToken } = options;
    const me = this,
      telemetryProps: CustomDimensions = {
        id: operationId,
        url: url,
        method: method,
        path: path,
        urlTemplate: urlTemplate,
        diagnosticContext: operationId
      };

    let prepareForFetch = Promise.resolve();

    if (!options.noBifrostToken) {
      prepareForFetch = prepareForFetch
        .then(() => {
          return HttpTokenUtils.getToken({ forceRefreshToken });
        })
        .then((token: UserToken) => {
          headers.append("Authorization", "Bearer " + token.jwtToken);
        });
    }

    return prepareForFetch.then(() => {
      return me._fetch<T>(url, requestOptions, options, telemetryProps);
    });
  }

  /**
   * Makes the fetch call.
   */
  private _fetch<T>(url: string, requestOptions: RequestInit, options: IHttpOptions, telemetryProps: CustomDimensions): Promise<T> {
    // Start the time lap
    const start = Timer.startLap();
    const beaconName = telemetry.beacon;

    return fetch(url, requestOptions).then(
      (response: Response): Promise<T> => {
        // End the lap
        Timer.endLapAndTrack(
          start,
          Object.assign({}, telemetryProps, {
            success: response.ok,
            status: response.status,
            beacon: beaconName
          })
        );

        // Check if response is success and do the appropriate thing.
        if (response.ok) {
          // Do not parse content if status returns 204 NO CONTENT
          if (response.status === 204) {
            return Promise.resolve() as any;
          }
          return Promise.resolve(response[options.responseType || RESPONSE_TYPES.JSON]());
        } else {
          // Throws an explicit error using the error body.
          return response.text().then((body) => {
            // Assume that the error is a JSON object.
            if (body.length > 0) {
              try {
                body = JSON.parse(body);
              } catch (exception) {
                // Ignore JSON parsing exception, use body as text.
              }
            }
            throw new FetchError(response, body);
          }) as any;
        }
      },
      (error: Error) => {
        // End the lap
        Timer.endLapAndTrack(
          start,
          Object.assign({}, telemetryProps, {
            success: false,
            beacon: beaconName
          })
        );

        // throw the error
        throw new FetchError({}, { error: { message: error.message } });
      }
    );
  }
}

export const apiGateway = new Http(Utils.isInstallSubdomain() ? config.getBifrostInstallHostUrl() : config.getBifrostHostUrl());
export const portalServer = new Http();
